import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Calendar,
  Car,
  ChevronRight,
  DollarSign,
  Download,
  FileText,
  Filter,
  Fuel,
  Leaf,
  Route as RouteIcon,
  Target,
  Timer,
  TrendingDown,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { useDocumentTitle } from '@/hooks/use-document-title'
import { formatCurrency, formatNumber } from '@/lib/format'
import { mockEcoScores } from '@/lib/mock-data'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute('/dashboard/eco-driving/')({
  head: () => ({ meta: [{ title: localizedPageTitle('ecoDriving') }] }),
  component: EcoDriving,
})

export default function EcoDriving() {
  useDocumentTitle('ecoDriving')
  const { t } = useTranslation('ecoDriving')
  const [selectedPeriod, setSelectedPeriod] = useState('30days')
  const [selectedDriver, setSelectedDriver] = useState<string | null>(null)
  const [selectedTab, setSelectedTab] = useState('overview')
  const [showReportSuccess, setShowReportSuccess] = useState(false)

  const dailyTrendData = [
    { date: '01', score: 76.2, fuelCost: 1245, distance: 892, incidents: 12 },
    { date: '02', score: 74.8, fuelCost: 1320, distance: 945, incidents: 15 },
    { date: '03', score: 78.1, fuelCost: 1189, distance: 867, incidents: 9 },
    { date: '04', score: 77.5, fuelCost: 1278, distance: 912, incidents: 11 },
    { date: '05', score: 79.3, fuelCost: 1156, distance: 834, incidents: 8 },
    { date: '06', score: 80.1, fuelCost: 1098, distance: 798, incidents: 7 },
    { date: '07', score: 78.9, fuelCost: 1134, distance: 845, incidents: 10 },
    { date: '08', score: 81.2, fuelCost: 1067, distance: 812, incidents: 6 },
    { date: '09', score: 79.8, fuelCost: 1112, distance: 856, incidents: 9 },
    { date: '10', score: 82.4, fuelCost: 1023, distance: 789, incidents: 5 },
    { date: '11', score: 81.7, fuelCost: 1045, distance: 823, incidents: 7 },
    { date: '12', score: 83.1, fuelCost: 998, distance: 767, incidents: 4 },
    { date: '13', score: 82.5, fuelCost: 1012, distance: 801, incidents: 6 },
    { date: '14', score: 84.2, fuelCost: 967, distance: 756, incidents: 3 },
  ]

  const monthlyComparisonData = [
    { month: 'Jul', actual: 74.2, target: 80, cost: 38450 },
    { month: 'Aug', actual: 76.8, target: 80, cost: 36890 },
    { month: 'Sep', actual: 78.1, target: 80, cost: 35120 },
    { month: 'Oct', actual: 79.5, target: 82, cost: 33780 },
    { month: 'Nov', actual: 81.3, target: 82, cost: 32450 },
    { month: 'Dec', actual: 82.7, target: 85, cost: 31200 },
  ]

  const fuelEfficiencyData = [
    { name: 'Week 1', consumption: 32.4, target: 30, cost: 8120 },
    { name: 'Week 2', consumption: 31.8, target: 30, cost: 7950 },
    { name: 'Week 3', consumption: 30.9, target: 30, cost: 7725 },
    { name: 'Week 4', consumption: 29.7, target: 30, cost: 7425 },
  ]

  const incidentsByType = [
    { type: 'Harsh Braking', count: 156, change: -12, severity: 'high' },
    { type: 'Harsh Acceleration', count: 98, change: -8, severity: 'medium' },
    { type: 'Excessive Idling', count: 234, change: -15, severity: 'low' },
    { type: 'Sharp Turns', count: 67, change: -5, severity: 'medium' },
    { type: 'Speeding', count: 45, change: +3, severity: 'high' },
    { type: 'Coasting', count: 189, change: +12, severity: 'low' },
  ]

  const handleExportData = () => {
    const csvContent = mockEcoScores
      .map(
        (score) =>
          `${score.driverName},${score.overallScore},${score.suddenBraking},${score.suddenAcceleration},${score.idleTime},${score.suddenTurns},${score.distanceTraveled},${score.drivingTime},${score.avgFuelConsumption},${score.ecoSpeed}`,
      )
      .join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `eco-driving-report.csv`
    a.click()
  }

  const handleCreateReport = () => {
    setShowReportSuccess(true)
    setTimeout(() => setShowReportSuccess(false), 3000)
  }

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'text-emerald-600 dark:text-emerald-400'
    if (score >= 75) return 'text-sky-600 dark:text-sky-400'
    if (score >= 65) return 'text-amber-600 dark:text-amber-400'
    return 'text-rose-600 dark:text-rose-400'
  }

  const getScoreBg = (score: number) => {
    if (score >= 85) return 'bg-emerald-600'
    if (score >= 75) return 'bg-sky-600'
    if (score >= 65) return 'bg-amber-600'
    return 'bg-rose-600'
  }

  const overallAvgScore = Math.round(
    mockEcoScores.reduce((sum, score) => sum + score.overallScore, 0) /
      mockEcoScores.length,
  )

  const sortedScores = [...mockEcoScores].sort(
    (a, b) => b.overallScore - a.overallScore,
  )

  const kpis = {
    fleetScore: overallAvgScore,
    totalDistance: 45678,
    fuelCost: 31200,
    fuelSaved: 2840,
    co2Reduced: 7120,
    totalIncidents: 789,
    avgEfficiency: 28.4,
    costPerKm: 0.68,
  }

  const tooltipStyle = {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '12px',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('title')}
          </h1>
          <p className="text-muted-foreground mt-0.5 text-sm">
            Fleet performance analytics and driver behavior monitoring
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="h-9 w-[130px] text-sm">
              <Calendar className="text-muted-foreground mr-1.5 h-3.5 w-3.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Last 7 days</SelectItem>
              <SelectItem value="30days">Last 30 days</SelectItem>
              <SelectItem value="90days">Last 90 days</SelectItem>
              <SelectItem value="12months">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="h-9 bg-transparent"
            onClick={handleExportData}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Export
          </Button>
          <Button size="sm" className="h-9" onClick={handleCreateReport}>
            <FileText className="mr-1.5 h-3.5 w-3.5" />
            Generate Report
          </Button>
        </div>
      </div>

      {showReportSuccess && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400">
          Report generated successfully. Download will start automatically.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="border-l-4 border-l-sky-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Fleet Score
                </p>
                <p className="mt-1 text-2xl font-semibold">{kpis.fleetScore}</p>
                <div className="mt-1 flex items-center gap-1">
                  <ArrowUpRight className="h-3 w-3 text-emerald-500" />
                  <span className="text-xs text-emerald-600">
                    +4.2% vs last period
                  </span>
                </div>
              </div>
              <div className="rounded-lg bg-sky-50 p-2.5 dark:bg-sky-900/20">
                <Target className="h-5 w-5 text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Fuel Saved
                </p>
                <p className="mt-1 text-2xl font-semibold">
                  {formatNumber(kpis.fuelSaved)} L
                </p>
                <div className="mt-1 flex items-center gap-1">
                  <ArrowUpRight className="h-3 w-3 text-emerald-500" />
                  <span className="text-xs text-emerald-600">
                    {formatCurrency(kpis.fuelSaved * 1.45)} saved
                  </span>
                </div>
              </div>
              <div className="rounded-lg bg-emerald-50 p-2.5 dark:bg-emerald-900/20">
                <Fuel className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Total Incidents
                </p>
                <p className="mt-1 text-2xl font-semibold">
                  {kpis.totalIncidents}
                </p>
                <div className="mt-1 flex items-center gap-1">
                  <ArrowDownRight className="h-3 w-3 text-emerald-500" />
                  <span className="text-xs text-emerald-600">
                    -18% reduction
                  </span>
                </div>
              </div>
              <div className="rounded-lg bg-amber-50 p-2.5 dark:bg-amber-900/20">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-slate-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Cost per km
                </p>
                <p className="mt-1 text-2xl font-semibold">€{kpis.costPerKm}</p>
                <div className="mt-1 flex items-center gap-1">
                  <ArrowDownRight className="h-3 w-3 text-emerald-500" />
                  <span className="text-xs text-emerald-600">
                    -€0.08 vs target
                  </span>
                </div>
              </div>
              <div className="rounded-lg bg-slate-100 p-2.5 dark:bg-slate-800">
                <DollarSign className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="bg-card flex items-center gap-3 rounded-lg border p-3">
          <Car className="text-muted-foreground h-4 w-4" />
          <div>
            <p className="text-muted-foreground text-xs">Active Vehicles</p>
            <p className="text-sm font-medium">24 / 28</p>
          </div>
        </div>
        <div className="bg-card flex items-center gap-3 rounded-lg border p-3">
          <Users className="text-muted-foreground h-4 w-4" />
          <div>
            <p className="text-muted-foreground text-xs">Active Drivers</p>
            <p className="text-sm font-medium">{mockEcoScores.length}</p>
          </div>
        </div>
        <div className="bg-card flex items-center gap-3 rounded-lg border p-3">
          <RouteIcon className="text-muted-foreground h-4 w-4" />
          <div>
            <p className="text-muted-foreground text-xs">Total Distance</p>
            <p className="text-sm font-medium">
              {formatNumber(kpis.totalDistance)} km
            </p>
          </div>
        </div>
        <div className="bg-card flex items-center gap-3 rounded-lg border p-3">
          <Leaf className="text-muted-foreground h-4 w-4" />
          <div>
            <p className="text-muted-foreground text-xs">CO2 Reduced</p>
            <p className="text-sm font-medium">
              {formatNumber(kpis.co2Reduced)} kg
            </p>
          </div>
        </div>
      </div>

      <Tabs
        value={selectedTab}
        onValueChange={setSelectedTab}
        className="space-y-4"
      >
        <TabsList className="bg-muted/50">
          <TabsTrigger value="overview" className="text-sm">
            Overview
          </TabsTrigger>
          <TabsTrigger value="drivers" className="text-sm">
            Driver Analysis
          </TabsTrigger>
          <TabsTrigger value="incidents" className="text-sm">
            Incidents
          </TabsTrigger>
          <TabsTrigger value="costs" className="text-sm">
            Cost Analysis
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Score Trend Chart */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-medium">
                      Performance Trend
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Daily fleet score and incident correlation
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    Last 14 days
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={dailyTrendData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="left"
                      domain={[70, 90]}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      domain={[0, 20]}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="score"
                      fill="#0ea5e9"
                      fillOpacity={0.1}
                      stroke="#0ea5e9"
                      strokeWidth={2}
                      name="Score"
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="incidents"
                      fill="#f59e0b"
                      radius={[2, 2, 0, 0]}
                      name="Incidents"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Fleet Score Gauge */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">
                  Fleet Score
                </CardTitle>
                <CardDescription className="text-xs">
                  Overall performance index
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center">
                  <div className="relative h-40 w-40">
                    <svg className="h-40 w-40 -rotate-90 transform">
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="#e2e8f0"
                        strokeWidth="10"
                        fill="none"
                      />
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="#0ea5e9"
                        strokeWidth="10"
                        fill="none"
                        strokeDasharray={`${(overallAvgScore / 100) * 440} 440`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-3xl font-semibold">
                        {overallAvgScore}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        / 100
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 w-full space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Target</span>
                      <span className="font-medium">85</span>
                    </div>
                    <Progress
                      value={overallAvgScore}
                      className="h-1.5 [&>div]:bg-sky-500"
                    />
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        Gap to target
                      </span>
                      <span className="font-medium text-amber-600">
                        -{85 - overallAvgScore} pts
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly comparison */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-medium">
                    Monthly Performance vs Target
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Score achievement against organizational targets
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthlyComparisonData} barGap={2}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e2e8f0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[60, 100]}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar
                    dataKey="actual"
                    fill="#0ea5e9"
                    radius={[3, 3, 0, 0]}
                    name="Actual"
                  />
                  <Bar
                    dataKey="target"
                    fill="#e2e8f0"
                    radius={[3, 3, 0, 0]}
                    name="Target"
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Drivers Tab */}
        <TabsContent value="drivers" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Driver Ranking */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-medium">
                    Driver Rankings
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="h-7 text-xs">
                    <Filter className="mr-1 h-3 w-3" />
                    Filter
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {sortedScores.map((driver, index) => (
                    <div
                      key={driver.driverId}
                      className={`hover:bg-muted/30 flex cursor-pointer items-center justify-between px-4 py-3 transition-colors ${
                        selectedDriver === driver.driverId
                          ? 'bg-sky-50 dark:bg-sky-900/10'
                          : ''
                      }`}
                      onClick={() =>
                        setSelectedDriver(
                          selectedDriver === driver.driverId
                            ? null
                            : driver.driverId,
                        )
                      }
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                            index === 0
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30'
                              : index === 1
                                ? 'bg-slate-100 text-slate-600 dark:bg-slate-800'
                                : index === 2
                                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30'
                                  : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {driver.driverName}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {driver.distanceTraveled.toFixed(0)} km •{' '}
                            {driver.drivingTime.toFixed(1)}h
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p
                            className={`text-lg font-semibold ${getScoreColor(driver.overallScore)}`}
                          >
                            {driver.overallScore}
                          </p>
                        </div>
                        <ChevronRight className="text-muted-foreground h-4 w-4" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Driver Details */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">
                  {selectedDriver
                    ? mockEcoScores.find((d) => d.driverId === selectedDriver)
                        ?.driverName
                    : 'Select a Driver'}
                </CardTitle>
                <CardDescription className="text-xs">
                  Detailed performance breakdown
                </CardDescription>
              </CardHeader>
              <CardContent>
                {selectedDriver ? (
                  <div className="space-y-4">
                    {(() => {
                      const driver = mockEcoScores.find(
                        (d) => d.driverId === selectedDriver,
                      )!
                      return (
                        <>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-muted/50 rounded-lg p-3">
                              <p className="text-muted-foreground text-xs">
                                Score
                              </p>
                              <p
                                className={`text-xl font-semibold ${getScoreColor(driver.overallScore)}`}
                              >
                                {driver.overallScore}
                              </p>
                            </div>
                            <div className="bg-muted/50 rounded-lg p-3">
                              <p className="text-muted-foreground text-xs">
                                ECO Speed
                              </p>
                              <p className="text-xl font-semibold">
                                {driver.ecoSpeed}%
                              </p>
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />
                                <span className="text-sm">Harsh Braking</span>
                              </div>
                              <span className="text-sm font-medium">
                                {driver.suddenBraking}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Zap className="h-3.5 w-3.5 text-amber-500" />
                                <span className="text-sm">
                                  Harsh Acceleration
                                </span>
                              </div>
                              <span className="text-sm font-medium">
                                {driver.suddenAcceleration}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Timer className="h-3.5 w-3.5 text-slate-500" />
                                <span className="text-sm">Idle Time</span>
                              </div>
                              <span className="text-sm font-medium">
                                {driver.idleTime} min
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Activity className="h-3.5 w-3.5 text-sky-500" />
                                <span className="text-sm">Sharp Turns</span>
                              </div>
                              <span className="text-sm font-medium">
                                {driver.suddenTurns}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Fuel className="h-3.5 w-3.5 text-emerald-500" />
                                <span className="text-sm">
                                  Fuel Consumption
                                </span>
                              </div>
                              <span className="text-sm font-medium">
                                {driver.avgFuelConsumption.toFixed(1)} L/100km
                              </span>
                            </div>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                ) : (
                  <div className="text-muted-foreground flex h-48 flex-col items-center justify-center">
                    <Users className="mb-2 h-8 w-8 opacity-50" />
                    <p className="text-sm">Select a driver to view details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Driver metrics table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">
                Detailed Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/30 border-b">
                      <th className="text-muted-foreground p-3 text-left text-xs font-medium">
                        Driver
                      </th>
                      <th className="text-muted-foreground p-3 text-center text-xs font-medium">
                        Score
                      </th>
                      <th className="text-muted-foreground p-3 text-center text-xs font-medium">
                        Braking
                      </th>
                      <th className="text-muted-foreground p-3 text-center text-xs font-medium">
                        Accel.
                      </th>
                      <th className="text-muted-foreground p-3 text-center text-xs font-medium">
                        Idle
                      </th>
                      <th className="text-muted-foreground p-3 text-center text-xs font-medium">
                        Distance
                      </th>
                      <th className="text-muted-foreground p-3 text-center text-xs font-medium">
                        Fuel
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {mockEcoScores.map((driver) => (
                      <tr
                        key={driver.driverId}
                        className="hover:bg-muted/20 border-b transition-colors"
                      >
                        <td className="p-3 font-medium">{driver.driverName}</td>
                        <td className="p-3 text-center">
                          <span
                            className={`font-semibold ${getScoreColor(driver.overallScore)}`}
                          >
                            {driver.overallScore}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          {driver.suddenBraking}
                        </td>
                        <td className="p-3 text-center">
                          {driver.suddenAcceleration}
                        </td>
                        <td className="p-3 text-center">{driver.idleTime}m</td>
                        <td className="p-3 text-center">
                          {driver.distanceTraveled.toFixed(0)} km
                        </td>
                        <td className="p-3 text-center">
                          {driver.avgFuelConsumption.toFixed(1)} L
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">
                  Incidents by Type
                </CardTitle>
                <CardDescription className="text-xs">
                  Current period breakdown
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {incidentsByType.map((incident) => (
                    <div
                      key={incident.type}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-2 w-2 rounded-full ${
                            incident.severity === 'high'
                              ? 'bg-rose-500'
                              : incident.severity === 'medium'
                                ? 'bg-amber-500'
                                : 'bg-slate-400'
                          }`}
                        />
                        <span className="text-sm">{incident.type}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">
                          {incident.count}
                        </span>
                        <div
                          className={`flex items-center gap-0.5 text-xs ${
                            incident.change < 0
                              ? 'text-emerald-600'
                              : 'text-rose-600'
                          }`}
                        >
                          {incident.change < 0 ? (
                            <TrendingDown className="h-3 w-3" />
                          ) : (
                            <TrendingUp className="h-3 w-3" />
                          )}
                          <span>{Math.abs(incident.change)}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">
                  Incident Trend
                </CardTitle>
                <CardDescription className="text-xs">
                  Daily incident count
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={dailyTrendData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area
                      type="monotone"
                      dataKey="incidents"
                      stroke="#f59e0b"
                      fill="#fef3c7"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Costs Tab */}
        <TabsContent value="costs" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">
                  Weekly Fuel Efficiency
                </CardTitle>
                <CardDescription className="text-xs">
                  Consumption vs target (L/100km)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={fuelEfficiencyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      domain={[25, 35]}
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar
                      dataKey="consumption"
                      fill="#0ea5e9"
                      radius={[3, 3, 0, 0]}
                      name="Actual"
                    />
                    <Bar
                      dataKey="target"
                      fill="#e2e8f0"
                      radius={[3, 3, 0, 0]}
                      name="Target"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">
                  Fuel Cost Trend
                </CardTitle>
                <CardDescription className="text-xs">
                  Daily fuel expenditure (€)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={dailyTrendData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Line
                      type="monotone"
                      dataKey="fuelCost"
                      stroke="#10b981"
                      strokeWidth={2}
                      dot={false}
                      name="Fuel Cost (€)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium">
                Monthly Cost Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={monthlyComparisonData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e2e8f0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar
                    yAxisId="right"
                    dataKey="cost"
                    fill="#64748b"
                    radius={[3, 3, 0, 0]}
                    name="Cost (€)"
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="actual"
                    stroke="#0ea5e9"
                    strokeWidth={2}
                    name="Score"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
